import { useEffect, useMemo, useState } from "react";
import {
  type HotelDTO,
  type UserSummary,
  getUsersApi,
  listHotels,
  listUsers,
  useCore,
} from "@praesto/core";
import {
  Toolbar,
  Input,
  Select,
  Button,
  Card,
  Banner,
  EmptyState,
  SkeletonList,
  SkeletonCardListItem,
  AvatarOrInitials,
  StatusBadge,
  Page,
  departmentNameResolver,
} from "@praesto/ui-kit";
import type { Department } from "@praesto/shared-types";
import { ROLE_LABEL, ROLE_ORDER, roleLabel } from "./roleLabels";
import { AddUserDropdown } from "./AddUserDropdown";
import { InviteUserModal } from "./InviteUserModal";
import { CreateUserModal } from "./CreateUserModal";

const PAGE_SIZE = 25;
/** How many people one server page carries — the endpoint's own maximum. */
const SERVER_PAGE_SIZE = 200;

/** Membership department codes as the names the hotels gave them. The row
 *  carries codes from every membership, so they resolve against the
 *  departments of all the tenant's hotels (one cached read per hotel). */
function formatDepartments(
  codes: ReadonlyArray<string> | undefined,
  deptLabel: (code: string) => string,
): string {
  if (!codes || codes.length === 0) return "—";
  return [...new Set(codes.map(deptLabel))].join(", ");
}

function formatStructure(
  hotelIds: ReadonlyArray<string> | undefined,
  hotels: ReadonlyArray<HotelDTO>,
): string {
  if (!hotelIds || hotelIds.length === 0) return "—";
  return hotelIds.map((id) => hotels.find((h) => h.id === id)?.name ?? id).join(", ");
}

export function PeopleList(props: { onEdit: (uid: string) => void }) {
  const core = useCore();
  const canManageUsers = core.rbac.can("manage", "user");
  const [users, setUsers] = useState<ReadonlyArray<UserSummary>>([]);
  const [hotels, setHotels] = useState<ReadonlyArray<HotelDTO>>([]);
  const [hotelFilter, setHotelFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  /** Server cursor. Present only while the tenant has more people than the
   *  first page returned; `undefined` means the roster is fully loaded. */
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [pwToast, setPwToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await listUsers(
        api.apiBase,
        {
          ...(hotelFilter ? { hotelId: hotelFilter } : {}),
          ...(roleFilter ? { role: roleFilter } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          // The server's maximum. Deliberately large: the search box below
          // filters what is LOADED, so a small first page would silently turn
          // "cerca una persona" into "cerca fra i primi 25". At this size any
          // realistic tenant arrives whole, and paging only engages for one
          // big enough that the button below is a visible, honest signal.
          limit: SERVER_PAGE_SIZE,
        },
        tok,
      );
      if (!cancelled) {
        setUsers(r.users);
        setCursor(r.nextCursor);
      }
    })()
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [hotelFilter, roleFilter, statusFilter, reload]);

  useEffect(() => {
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await listHotels(api.apiBase, tok);
      setHotels(r.hotels);
    })().catch(() => undefined);
  }, []);

  const [departments, setDepartments] = useState<ReadonlyArray<Department>>([]);
  useEffect(() => {
    if (hotels.length === 0) return;
    let cancelled = false;
    void Promise.all(hotels.map((h) => core.organization.departments(h.id).catch(() => []))).then(
      (lists) => {
        if (!cancelled) setDepartments(lists.flat());
      },
    );
    return () => {
      cancelled = true;
    };
  }, [core, hotels]);
  const deptLabel = useMemo(() => departmentNameResolver(departments), [departments]);

  // Client-side search by name/email. Paginate the result so the DOM never
  // renders hundreds of cards at once (the page grew unbounded before).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) => (u.displayName ?? "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [users, search]);

  // Reset the visible window whenever the filtered set changes.
  // Reset the visible window when the QUERY changes — not when `users` grows.
  // Keying this on `users` too made every appended server page snap the window
  // back to the first 25 rows, undoing the click that fetched them.
  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [search, hotelFilter, roleFilter, statusFilter]);

  const shown = filtered.slice(0, visible);
  /** More rows to reveal locally, or more people still on the server. */
  const hasMore = filtered.length > visible || cursor !== undefined;

  function loadMore(): void {
    // Reveal what is already loaded first; only reach for the network once the
    // local buffer is exhausted.
    if (filtered.length > visible) {
      setVisible((v) => v + PAGE_SIZE);
      return;
    }
    if (cursor === undefined || loadingMore) return;
    setLoadingMore(true);
    void (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const r = await listUsers(
        api.apiBase,
        {
          ...(hotelFilter ? { hotelId: hotelFilter } : {}),
          ...(roleFilter ? { role: roleFilter } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          limit: SERVER_PAGE_SIZE,
          after: cursor,
        },
        tok,
      );
      setUsers((prev) => [...prev, ...r.users]);
      setCursor(r.nextCursor);
      setVisible((v) => v + PAGE_SIZE);
    })()
      .catch((e) => setError(String(e)))
      .finally(() => setLoadingMore(false));
  }

  return (
    <Page
      density="editorial"
      eyebrow="PERSONE"
      title="Chi lavora, e con che contratto"
      description="Ruoli, contratti e competenze delle persone della struttura."
      actions={
        canManageUsers ? (
          <AddUserDropdown
            onInvite={() => setShowInvite(true)}
            onCreate={() => setShowCreate(true)}
          />
        ) : undefined
      }
    >
      <Toolbar
        filters={
          <>
            <Input
              size="md"
              placeholder="Cerca per nome o email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select size="md" value={hotelFilter} onChange={(e) => setHotelFilter(e.target.value)}>
              <option value="">Tutti gli hotel</option>
              {hotels.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
            <Select size="md" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">Tutti i ruoli</option>
              {ROLE_ORDER.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
            <Select
              size="md"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Tutti gli stati</option>
              <option value="pending">Invitato</option>
              <option value="active">Attivo</option>
              <option value="disabled">Disattivato</option>
            </Select>
          </>
        }
      />
      {loading && <SkeletonList count={6} item={<SkeletonCardListItem />} />}
      {error && error.includes("403") ? (
        <EmptyState
          title="Accesso non autorizzato"
          description="Non hai i permessi per vedere la lista delle persone. Chiedi all'amministratore di assegnarti il ruolo necessario."
        />
      ) : error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : null}
      {!loading && filtered.length === 0 && !error ? (
        <EmptyState
          title={search ? "Nessuna corrispondenza" : "Nessuna persona"}
          description={
            search
              ? `Nessuna persona corrisponde a "${search}": prova con un altro nome o svuota i filtri.`
              : "Aggiungi la prima persona con «Aggiungi», o invitala via email."
          }
        />
      ) : !error ? (
        <>
          {!loading && (
            <div className="mb-2 text-mono-xs text-fg-muted">
              {filtered.length} {filtered.length === 1 ? "persona" : "persone"}
            </div>
          )}
          <div className="space-y-2">
            {shown.map((u) => (
              <Card key={u.id} padding="sm" hoverable>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => props.onEdit(u.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      props.onEdit(u.id);
                    }
                  }}
                  className="flex items-center gap-3 cursor-pointer focus:outline-none focus-visible:shadow-focus rounded-md"
                >
                  <div className="flex items-center gap-3 min-w-0 sm:w-56 sm:flex-shrink-0">
                    <AvatarOrInitials displayName={u.displayName || u.email} size={32} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-fg-default truncate">
                        {u.displayName || u.email}
                      </div>
                      <div className="text-mono-xs text-fg-muted truncate">{u.email}</div>
                    </div>
                  </div>
                  {/* Membership facts fill the middle so the row reads left-to-right
                      instead of leaving a gap between name and status at full width.
                      Hidden below sm — mobile keeps the tighter name/status row. */}
                  <div className="hidden sm:grid sm:flex-1 sm:grid-cols-3 sm:gap-4 sm:min-w-0 text-xs text-fg-muted">
                    <div className="truncate">{u.role ? roleLabel(u.role) : "—"}</div>
                    <div className="truncate">{formatDepartments(u.departments, deptLabel)}</div>
                    <div className="truncate">{formatStructure(u.hotelIds, hotels)}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                    <StatusBadge status={u.status as "active" | "pending" | "disabled"} />
                    {canManageUsers && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="hidden sm:inline-flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onEdit(u.id);
                        }}
                      >
                        Modifica
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {hasMore && (
            <Button
              size="md"
              variant="secondary"
              className="mt-4 w-full sm:w-auto"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {/* With rows already buffered locally the count is knowable, so it is
                  shown. When the buffer is spent the next click goes to the server,
                  and a filter can reduce a full page to nothing visible — so the
                  label says where the rows are coming from rather than promising a
                  number it cannot know. */}
              {loadingMore
                ? "Caricamento…"
                : filtered.length > visible
                  ? `Carica altri (${filtered.length - visible})`
                  : "Carica altri dal server"}
            </Button>
          )}
        </>
      ) : null}
      <Button
        size="md"
        variant="ghost"
        className="mt-4 ml-2"
        onClick={() => setReload((r) => r + 1)}
      >
        Aggiorna
      </Button>
      {showInvite && (
        <InviteUserModal
          hotels={hotels}
          onClose={() => setShowInvite(false)}
          onDone={() => {
            setShowInvite(false);
            setReload((r) => r + 1);
          }}
        />
      )}
      {showCreate && (
        <CreateUserModal
          hotels={hotels}
          onClose={() => setShowCreate(false)}
          onDone={(uid, pw) => {
            setShowCreate(false);
            setPwToast(`Utente creato (uid ${uid}). Password da comunicare: ${pw}`);
            setReload((r) => r + 1);
          }}
        />
      )}
      {/* A password to hand over is a condition that lasts until the manager
          has copied it — a Banner in the flow, not a Toast that vanishes. */}
      {pwToast && (
        <Banner tone="success" className="mt-4" onDismiss={() => setPwToast(null)}>
          {pwToast}
        </Banner>
      )}
    </Page>
  );
}
